import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  cancelPblCase,
  createPblDocument,
  getPblCase,
  getPblDocument,
  inviteFirstPblCaseMatch,
  recordPblCaseMatchingExposure,
  reassignPblCaseLawyer,
  runPblCaseMatching,
  uploadPblDocumentFile,
} from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import {
  CancelCaseConfirmModal,
  ContractNegotiationPanel,
  DocumentDetailModal,
  getDocumentFilePath,
  ReassignLawyerModal,
  type CancelCaseInput,
  type ReassignLawyerInput,
} from '@/components/Pbl/CaseProgressDetail';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import useResize from '@/hooks/useResize';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import PATH from '@/router/path';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import { openStorageDownload } from '@/utils/storageDownload';
import { getUserPblRoles, isPblAdmin, isPblCitizen, isPblLawyer, isPblOcAdminOrStaff } from '@/utils/pblRole';
import { isPblAssistanceActive } from '@/utils/pblAssistance';
import { translatePblDisplayList } from '@/utils/pblDisplayTranslation';

import { getUploadedPblFileId } from '@/components/Pbl/VolunteerRegistration/uploadedDocument';
import { extensionValidator } from '@/utils/extensionValidator';
import { fileSizeValidator } from '@/utils/fileSizeValidator';
import { validateFilename } from '@/utils/filenameValidator';
import PblCitizenCaseFeedback from '../CitizenCaseFeedback';
import PblCaseProgressDetail from '../CaseProgressDetail';
import {
  buildPblDocumentCreatePayload,
  canClosePblCaseStatus,
  isPblReadOnlyCaseStatus,
} from '../CaseProgressDetail/caseProgressState';
import { fakePblInProgressCase, fakePblPendingEvaluationCase } from '../CaseProgressDetail/fakeData';
import { CaseInformationModal } from './CaseInformationModal';
import MatchingProgressPanel from './MatchingProgressPanel';
import { getInitialMatchingView, isVisiblePblDirectoryEntry, mapDirectoryEntryToOcLawyer, mapLawyerProfileToOcLawyer } from './caseMatchingState';
import { mapPblCaseToOcManagedCase } from './caseReviewState';
import LawyerReviewSummaryModal from './LawyerReviewSummaryModal';
import {
  ActionRow,
  CaseInfoAction,
  ChoiceWarningNotice,
  CvRow,
  CvTitle,
  DeclinedIntro,
  DeclinedTable,
  DetailSectionPanel,
  InfoGrid,
  InfoLabel,
  InfoValue,
  InvitationNotice,
  LawyerCard,
  LawyerDivider,
  LawyerMetaGrid,
  LawyerName,
  MatchNoticeBox,
  ModalAction,
  ModalSurface,
  ModalTitle,
  SectionTitle,
  TextLinkButton,
  TitleBar,
} from './styles';
import type { PblOcLawyer, PblOcMatchingView } from './types';
import type { PblCase, PblCaseStatus, PblDocument } from '@/types/pbl';
import { useTranslation } from 'react-i18next';
import { convertLink } from '@/utils/convert';
import { isPblMatchingProcessing, usePblMatchingStatusPolling } from './usePblMatchingStatusPolling';
import { usePblInvitationPolling } from './usePblInvitationPolling';

const buildCaseLawyersPath = (caseId: string) => PATH.pbl.caseLawyers.replace(':caseId', caseId);
const buildLawyerMeetingCalendarPath = (caseId: string | number) =>
  PATH.pbl.lawyerMeetingCalendar.replace(':caseId', String(caseId));

const pblMatchingRunStatuses: PblCaseStatus[] = [
  'initiated',
  'matching_ready',
  'ai_matching_in_progress',
  'ai_matched',
];

const pblCaseProgressWorkspaceStatuses: PblCaseStatus[] = [
  'lawyer_accepted',
  'oc_checking',
  'pending_lawyer_timeslot',
  'pending_appointment_confirmation',
];

const pblCitizenPreparationWorkspaceStatuses: PblCaseStatus[] = [
  'initiated',
  'matching_ready',
  'ai_matching_in_progress',
  'ai_matched',
  'manual_assignment_required',
  'lawyer_invited',
  'pending_lawyer_response',
  'pending_reassignment',
];

const pblInviteFirstMatchedStatuses: PblCaseStatus[] = [
  'matching_ready',
  'ai_matched',
  'manual_assignment_required',
];

const pblManualLawyerChoiceStatuses: PblCaseStatus[] = [
  'ai_matched',
  'manual_assignment_required',
];

const pblReassignLawyerStatuses: PblCaseStatus[] = [
  'lawyer_accepted',
  'oc_checking',
  'pending_lawyer_timeslot',
  'pending_appointment_confirmation',
  'meeting_scheduled',
  'in_progress',
  'pending_documents',
  'pending_completion',
  'pending_evaluation',
];

const pblInactiveAssignmentStatuses = new Set(['declined', 'timeout', 'reassigned', 'removed', 'cancelled']);

type PblUploadRole = 'pbl_lawyer' | 'oc_admin' | 'oc_staff' | 'citizen';

const getNumericUserId = (userData: any) => {
  const userId = Number(userData?.id);

  return Number.isFinite(userId) ? userId : undefined;
};

const getPblUploadRole = (userData: any): PblUploadRole => {
  const roles = getUserPblRoles(userData);

  if (roles.includes('pbl_lawyer')) return 'pbl_lawyer';
  if (roles.includes('oc_staff')) return 'oc_staff';
  if (roles.includes('oc_admin') || roles.includes('pbl_admin')) return 'oc_admin';
  return 'citizen';
};

const isPblSharedWorkspaceStatus = (status?: PblCaseStatus | string | null) =>
  canClosePblCaseStatus(status) ||
  isPblReadOnlyCaseStatus(status) ||
  pblCaseProgressWorkspaceStatuses.includes(status as PblCaseStatus);

const isPblCitizenWorkspaceStatus = (status?: PblCaseStatus | string | null) =>
  isPblSharedWorkspaceStatus(status) ||
  pblCitizenPreparationWorkspaceStatuses.includes(status as PblCaseStatus);

const isRatingAlertForCurrentUser = (caseData: PblCase, userData: unknown, assistedCitizen = false) => {
  if (caseData.rating_alert?.show !== true) return false;
  if (caseData.status !== 'pending_evaluation' && caseData.status !== 'closed') return false;

  const evaluatorRole = caseData.rating_alert.evaluator_role;

  if (evaluatorRole === 'citizen') return isPblCitizen(userData) || assistedCitizen;
  if (evaluatorRole === 'pbl_lawyer') return isPblLawyer(userData);

  return isPblCitizen(userData) || assistedCitizen || isPblLawyer(userData);
};

const shouldRunMatchingForCase = (item?: PblCase | null) =>
  Boolean(
    item &&
      pblMatchingRunStatuses.includes(item.status) &&
      !['queued', 'running'].includes(item.matching_experiment?.status || '') &&
      !item.matching_results?.length &&
      !item.assignments?.some((assignment) => assignment.status === 'invited' || assignment.status === 'pending')
  );

const trimDisplayText = (value?: string | null) => value?.trim() || '';

const isChinesePblLanguage = (language?: string) => {
  const normalizedLanguage = (language || '').toLowerCase();
  return normalizedLanguage === 'tc' || normalizedLanguage === 'sc' || normalizedLanguage.startsWith('zh');
};

const pickLocalizedCvText = (english?: string | null, chinese?: string | null, language?: string) => {
  const englishText = trimDisplayText(english);
  const chineseText = trimDisplayText(chinese);

  return isChinesePblLanguage(language) ? chineseText || englishText : englishText || chineseText;
};

const getLawyerDisplayQualification = (lawyer: PblOcLawyer, language?: string) =>
  pickLocalizedCvText(
    lawyer.profile?.cv_other_qualifications_en,
    lawyer.profile?.cv_other_qualifications_zh,
    language
  ) || lawyer.qualification;

const getLawyerDisplayLanguages = (lawyer: PblOcLawyer, language?: string) =>
  pickLocalizedCvText(lawyer.profile?.cv_languages_en, lawyer.profile?.cv_languages_zh, language) || lawyer.languages;

const getLawyerDisplayPrimaryPracticeArea = (lawyer: PblOcLawyer, language?: string) =>
  pickLocalizedCvText(
    lawyer.profile?.cv_primary_practice_areas_en,
    lawyer.profile?.cv_primary_practice_areas_zh,
    language
  ) || lawyer.primaryPracticeArea;

const LawyerProfileCard = ({
  lawyer,
  onPreview,
}: {
  lawyer: PblOcLawyer;
  onPreview: (language: 'en' | 'zh') => void;
}) => {
  //   @ts-ignore
  const { t, i18n } = useTranslation();
  const displayQualification = getLawyerDisplayQualification(lawyer, i18n.language);
  const displayLanguages = getLawyerDisplayLanguages(lawyer, i18n.language);
  const displayPrimaryPracticeArea = getLawyerDisplayPrimaryPracticeArea(lawyer, i18n.language);
  const admittedYears = lawyer.admittedYears === null ? '-' : t('pbl.lawyerMatching.caseDetail.lawyerCard.years', {
    count: lawyer.admittedYears,
    defaultValue: '{{count}} years',
  });
  return (
  <LawyerCard>
    <LawyerName>{lawyer.name}</LawyerName>
    <LawyerMetaGrid>
      <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.professionalQualificationCertifications')}</InfoLabel>
      <InfoValue>{translatePblDisplayList(t, displayQualification)}</InfoValue>
      <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.admittedYears')}</InfoLabel>
      <InfoValue>{admittedYears}</InfoValue>
      <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.languages')}</InfoLabel>
      <InfoValue>{translatePblDisplayList(t, displayLanguages)}</InfoValue>
      <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.primaryPracticeArea')}</InfoLabel>
      <InfoValue>{translatePblDisplayList(t, displayPrimaryPracticeArea)}</InfoValue>
      <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.completedProBonoCaseS')}</InfoLabel>
      <InfoValue>{lawyer.completedCases}</InfoValue>
    </LawyerMetaGrid>
    <LawyerDivider />
    <CvTitle>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.cvOfTheProbonoLawyer')}</CvTitle>
    <CvRow>
      <Button type="button" size="md" variant="primary" rounded={false} onClick={() => onPreview('en')}>
        {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.detailsOfTheProbonoLawyer')}</Button>
    </CvRow>
  </LawyerCard>
);
};

const PblOcManageCaseDetail = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const auth = useAppSelector(getAuthSelector);
  const { isMobile, isTablet } = useResize();
  const [backendCase, setBackendCase] = useState<PblCase | null>(null);
  const item = useMemo(() => (backendCase ? mapPblCaseToOcManagedCase(backendCase) : null), [backendCase]);
  const initialView = useMemo(
    () => (item ? getInitialMatchingView(item, searchParams) : ({ mode: 'ai_recommended' } as PblOcMatchingView)),
    [item, searchParams]
  );
  const [view, setView] = useState<PblOcMatchingView>(initialView);
  const [previewLanguage, setPreviewLanguage] = useState<'en' | 'zh'>('en');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isCancellingCase, setIsCancellingCase] = useState(false);
  const [isReassigningLawyer, setIsReassigningLawyer] = useState(false);
  const [isRunningMatching, setIsRunningMatching] = useState(false);
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [showCancelCaseConfirm, setShowCancelCaseConfirm] = useState(false);
  const [showReassignLawyer, setShowReassignLawyer] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const uploadFilesRef = useRef<File[] | null>(null);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);
  const [documentDetail, setDocumentDetail] = useState<PblDocument | null>(null);
  const [loadingDocumentId, setLoadingDocumentId] = useState<number | null>(null);
  const matchingHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const recommendationRef = useRef<HTMLDivElement | null>(null);
  const didFocusMatchingProgressRef = useRef(false);
  const exposureRecordedRef = useRef(false);
  const isCitizenUser = isPblCitizen(auth.userData) || isPblAssistanceActive();
  const isLawyerUser = isPblLawyer(auth.userData);
  const canManageLifecycle = isPblOcAdminOrStaff(auth.userData) || isPblAdmin(auth.userData);
  const hasRatingAlertForCurrentUser = backendCase ? isRatingAlertForCurrentUser(backendCase, auth.userData, isCitizenUser) : false;
  const invitationPolling = usePblInvitationPolling(backendCase, canManageLifecycle && !isCitizenUser, (next, expected) => {
    setBackendCase((current) => current === expected ? next : current);
  });
  const shouldOpenCitizenRatingForm = Boolean(
    backendCase &&
      isCitizenUser &&
      (backendCase.status === 'pending_evaluation' ||
        (backendCase.status === 'closed' &&
          hasRatingAlertForCurrentUser &&
          searchParams.get('status') === 'to_be_rated'))
  );
  const shouldShowClosedRatingWorkspace = Boolean(
    backendCase && backendCase.status === 'closed' && hasRatingAlertForCurrentUser
  );
  const shouldShowCaseProgressWorkspace = Boolean(
    backendCase &&
      (shouldShowClosedRatingWorkspace ||
        (isCitizenUser
          ? isPblCitizenWorkspaceStatus(backendCase.status)
          : isPblSharedWorkspaceStatus(backendCase.status)))
  );
  const assignmentLawyer =
    backendCase?.assignments
      ?.filter((assignment) => !pblInactiveAssignmentStatuses.has(assignment.status))
      ?.map((assignment) => mapLawyerProfileToOcLawyer(assignment.lawyer_profile))
      .find((profileLawyer): profileLawyer is PblOcLawyer => Boolean(profileLawyer)) || null;
  const matchedLawyer =
    backendCase?.matching_results
      ?.map((result) =>
        result.directory_entry
          ? isVisiblePblDirectoryEntry(result.directory_entry)
            ? mapDirectoryEntryToOcLawyer(result.directory_entry)
            : null
          : mapLawyerProfileToOcLawyer(result.lawyer_profile)
      )
      .find((profileLawyer): profileLawyer is PblOcLawyer => Boolean(profileLawyer)) || null;
  const hasMatchingResults = Boolean(backendCase?.matching_results?.length);
  const hasCurrentAssignment = Boolean(
    backendCase?.assignments?.some((assignment) => !pblInactiveAssignmentStatuses.has(assignment.status))
  );
  const refreshCaseAfterMatching = useCallback(async () => {
    if (!backendCase?.id || document.hidden) return;
    const response = await getPblCase(Number(backendCase.id));
    if (response.data.success !== false) setBackendCase(response.data.data);
  }, [backendCase?.id]);
  const matchingPollingEnabled = Boolean(
    canManageLifecycle &&
      !isCitizenUser &&
      !hasCurrentAssignment &&
      isPblMatchingProcessing(backendCase?.status, backendCase?.matching_experiment?.status)
  );
  const matchingPolling = usePblMatchingStatusPolling({
    caseId: backendCase?.id,
    enabled: matchingPollingEnabled,
    initialCaseStatus: backendCase?.status,
    initialMatching: backendCase?.matching_experiment,
    onTerminal: refreshCaseAfterMatching,
  });
  const effectiveCaseStatus = matchingPolling.status?.case_status || backendCase?.status;
  const effectiveMatchingStatus = matchingPolling.status?.matching?.status || backendCase?.matching_experiment?.status;
  const isMatchingProcessing = !hasCurrentAssignment && isPblMatchingProcessing(effectiveCaseStatus, effectiveMatchingStatus);
  const isMatchingFailed = !hasCurrentAssignment && effectiveMatchingStatus === 'failed';
  const isMatchingCompletedWithoutResult = !hasCurrentAssignment && effectiveMatchingStatus === 'completed' && !hasMatchingResults;
  const acceptedAssignmentId = backendCase?.assignments?.find((assignment) => assignment.status === 'accepted')?.id;
  const isPendingReassignment = backendCase?.status === 'pending_reassignment';
  const isManualAssignmentRequired = backendCase?.status === 'manual_assignment_required' && !hasCurrentAssignment;
  const showMatchingContext = Boolean(matchedLawyer && !hasCurrentAssignment && !isManualAssignmentRequired);
  const displayLawyer = isManualAssignmentRequired
    ? null
    : view.mode === 'declined' && matchedLawyer
      ? matchedLawyer
      : assignmentLawyer || (hasCurrentAssignment ? null : matchedLawyer);
  const canInviteFirstMatchedLawyer = Boolean(
    canManageLifecycle &&
      !isManualAssignmentRequired &&
      backendCase?.status &&
      pblInviteFirstMatchedStatuses.includes(backendCase.status) &&
      hasMatchingResults &&
      matchedLawyer &&
      !isMatchingProcessing &&
      !hasCurrentAssignment
  );
  const canRunMatchingAgain = Boolean(
      canManageLifecycle &&
      isPendingReassignment &&
      !isMatchingProcessing &&
      !hasCurrentAssignment
  );
  const canChooseAnotherLawyer = Boolean(
    canManageLifecycle &&
    backendCase?.status &&
      !isMatchingProcessing &&
      pblManualLawyerChoiceStatuses.includes(backendCase.status)
  );
  const canReassignLawyer = Boolean(
    canManageLifecycle &&
      acceptedAssignmentId &&
      backendCase?.status &&
      pblReassignLawyerStatuses.includes(backendCase.status)
  );
  const canCancelCase = Boolean(
    canManageLifecycle &&
      backendCase?.status &&
      !pblInviteFirstMatchedStatuses.includes(backendCase.status) &&
      !isPblReadOnlyCaseStatus(backendCase.status)
  );
  const canUseInvitationDocumentUploads = Boolean(
    canManageLifecycle &&
      backendCase &&
      view.mode === 'invitation_sent' &&
      !isPblReadOnlyCaseStatus(backendCase.status)
  );
  const shouldRedirectCitizenToCaseList = Boolean(
    isCitizenUser &&
      backendCase &&
      !isPblCitizenWorkspaceStatus(backendCase.status) &&
      !shouldOpenCitizenRatingForm &&
      !shouldShowClosedRatingWorkspace
  );
  const shouldRedirectLawyerToMeetingCalendar = Boolean(
    isLawyerUser && backendCase?.status === 'pending_lawyer_timeslot'
  );

  useEffect(() => {
    let isMounted = true;
    const numericCaseId = Number(caseId);

    const loadCase = async () => {
      if (process.env.NODE_ENV === 'development' && !auth.isLogin) {
        const fakeCase =
          numericCaseId === fakePblPendingEvaluationCase.id ||
          searchParams.get('status') === 'pending_evaluation' ||
          searchParams.get('status') === 'to_be_rated'
            ? fakePblPendingEvaluationCase
            : fakePblInProgressCase;

        setBackendCase({
          ...fakeCase,
          id: Number.isFinite(numericCaseId) ? numericCaseId : fakeCase.id,
        });
        return;
      }

      if (!Number.isFinite(numericCaseId)) {
        errorHandler(t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.invalidCaseId'));
        return;
      }

      try {
        const response = await getPblCase(numericCaseId);

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToLoadCase'));
          return;
        }

        let nextCase = response.data.data;

        if (!isCitizenUser && shouldRunMatchingForCase(nextCase)) {
          const matchingResponse = await runPblCaseMatching(numericCaseId);

          if (matchingResponse.data.success === false) {
            errorHandler(matchingResponse.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToRunLawyerMatching'));
          } else {
            const refreshedResponse = await getPblCase(numericCaseId);

            if (refreshedResponse.data.success !== false) {
              nextCase = refreshedResponse.data.data;
            }
          }
        }

        if (isMounted) setBackendCase(nextCase);
      } catch (error: any) {
        if (isMounted) {
          errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToLoadCase'));
        }
      }
    };

    loadCase();

    return () => {
      isMounted = false;
    };
  }, [auth.isLogin, caseId, isCitizenUser, searchParams]);

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  useEffect(() => {
    if (
      isMatchingProcessing &&
      !didFocusMatchingProgressRef.current &&
      Boolean((location.state as { focusMatchingProgress?: boolean } | null)?.focusMatchingProgress)
    ) {
      didFocusMatchingProgressRef.current = true;
      window.requestAnimationFrame(() => matchingHeadingRef.current?.focus());
    }
  }, [isMatchingProcessing, location.state]);

  useEffect(() => {
    exposureRecordedRef.current = false;
  }, [backendCase?.id]);

  useEffect(() => {
    const recommendation = recommendationRef.current;
    if (!recommendation || !backendCase?.id || !showMatchingContext || !canManageLifecycle) return undefined;

    let requestInFlight = false;
    const controller = new AbortController();
    const recordExposure = async () => {
      if (document.hidden || exposureRecordedRef.current || requestInFlight) return;
      requestInFlight = true;
      try {
        const response = await recordPblCaseMatchingExposure(Number(backendCase.id), controller.signal);
        if (response.data.success !== false) exposureRecordedRef.current = true;
      } catch (error: any) {
        if (error?.code !== 'ERR_CANCELED') requestInFlight = false;
      }
    };

    if (typeof IntersectionObserver === 'undefined') {
      void recordExposure();
      return () => controller.abort();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void recordExposure();
        }
      },
      { threshold: [0] }
    );
    observer.observe(recommendation);

    const handleVisibility = () => {
      if (!document.hidden) void recordExposure();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      controller.abort();
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [backendCase?.id, canManageLifecycle, showMatchingContext]);

  useEffect(() => {
    if (shouldRedirectCitizenToCaseList) {
      navigate(PATH.pbl.citizenCases, { replace: true });
    }
  }, [navigate, shouldRedirectCitizenToCaseList]);

  useEffect(() => {
    if (shouldRedirectLawyerToMeetingCalendar && backendCase) {
      navigate(buildLawyerMeetingCalendarPath(backendCase.id), { replace: true });
    }
  }, [backendCase, navigate, shouldRedirectLawyerToMeetingCalendar]);

  const chooseAnotherLawyer = () => {
    if (!item) return;
    navigate(buildCaseLawyersPath(String(item.id)));
  };
  const openPreview = (language: 'en' | 'zh') => {
    setPreviewLanguage(language);
    setPreviewOpen(true);
  };
  const runMatchingAgain = async () => {
    if (!item || isRunningMatching) return;
    if (!canRunMatchingAgain) {
      errorHandler(t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.matchingCannotBeRunAgainForThisCase'));
      return;
    }

    setIsRunningMatching(true);

    try {
      const response = await runPblCaseMatching(Number(item.id));

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToRunLawyerMatching'));
        return;
      }

      const refreshedCase = await getPblCase(Number(item.id));
      if (refreshedCase.data.success === false) {
        errorHandler(refreshedCase.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToRefreshCaseAfterMatching'));
        return;
      }

      setBackendCase(refreshedCase.data.data);
      setView(getInitialMatchingView(mapPblCaseToOcManagedCase(refreshedCase.data.data), searchParams));
      successHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.matchingStarted'));
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToRunLawyerMatching'));
    } finally {
      setIsRunningMatching(false);
    }
  };
  const sendInvitation = async () => {
    if (!item || isInviting) return;
    if (!canInviteFirstMatchedLawyer) {
      errorHandler(t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.noMatchedLawyerIsAvailablePleaseChooseA'));
      return;
    }

    setIsInviting(true);

    try {
      const response = await inviteFirstPblCaseMatch(Number(item.id));

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToSendInvitation'));
        return;
      }

      const refreshedCase = await getPblCase(Number(item.id));
      if (refreshedCase.data.success !== false) {
        setBackendCase(refreshedCase.data.data);
      }
      setView({ mode: 'invitation_sent', lawyerId: matchedLawyer?.id || displayLawyer?.id });
      setShowInviteConfirm(false);
      successHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.invitationSent'));
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToSendInvitation'));
    } finally {
      setIsInviting(false);
    }
  };

  const openInviteConfirm = () => {
    if (!canInviteFirstMatchedLawyer) {
      errorHandler(t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.noMatchedLawyerIsAvailablePleaseChooseA'));
      return;
    }

    setShowInviteConfirm(true);
  };

  const confirmCancelCase = async ({ reason }: CancelCaseInput) => {
    if (!item || isCancellingCase) return;

    setIsCancellingCase(true);

    try {
      const response = await cancelPblCase(Number(item.id), {
        reason,
      });

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToCancelTheCase'));
        return;
      }

      setBackendCase(response.data.data);
      setShowCancelCaseConfirm(false);
      successHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.caseCancelled'));
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToCancelTheCase'));
    } finally {
      setIsCancellingCase(false);
    }
  };

  const confirmReassignLawyer = async ({ reason }: ReassignLawyerInput) => {
    if (!item || isReassigningLawyer) return;

    setIsReassigningLawyer(true);

    try {
      const response = await reassignPblCaseLawyer(Number(item.id), {
        reason,
        ...(acceptedAssignmentId ? { assignment_id: acceptedAssignmentId } : {}),
      });

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToReassignLawyer'));
        return;
      }

      setShowReassignLawyer(false);
      successHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.caseReassigned'));
      navigate(PATH.pbl.cases, { replace: true });
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.failedToReassignLawyer'));
    } finally {
      setIsReassigningLawyer(false);
    }
  };

  const inspectDocument = useCallback(
    async (document: PblDocument) => {
      setLoadingDocumentId(document.id);
      setDocumentDetail(document);
      setShowDocumentDetail(true);

      try {
        const response = await getPblDocument(document.id);

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToLoadDocumentDetail'));
          return;
        }

        setDocumentDetail(response.data.data);
      } catch (error: any) {
        if (error.response?.status === 403) {
          errorHandler(t('pbl.pagesHardcoded.caseProgressDetail.noPermissionToViewThisDocument'));
          setShowDocumentDetail(false);
          return;
        }

        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToLoadDocumentDetail'));
      } finally {
        setLoadingDocumentId(null);
      }
    },
    [t]
  );

  const downloadDocument = useCallback(
    (document: PblDocument) => {
      const filePath = getDocumentFilePath(document);

      if (!filePath) {
        errorHandler('No file is available for download.');
        return;
      }

      void openStorageDownload(filePath);
    },
    [t]
  );

  const onDragStateChange = useCallback((_dragActive: boolean) => {}, []);

  const onFilesDrop = useCallback((selectedFiles: File[]) => {
    setFiles(selectedFiles);
  }, []);

  const uploadInvitationDocuments = useCallback(
    async (selectedFiles: File[]) => {
      if (selectedFiles.length === 0) return;

      if (!backendCase || !canUseInvitationDocumentUploads) {
        errorHandler(t('pbl.caseDetailDocuments.messages.casePartyActionDenied'));
        setFiles([]);
        return;
      }

      setIsUploadingDocument(true);

      try {
        if (!extensionValidator(selectedFiles)) {
          errorHandler(t('error.extension'));
          return;
        }

        if (!validateFilename(selectedFiles)) {
          errorHandler(t('error.invalidFileName'));
          return;
        }

        if (selectedFiles.some((file) => !fileSizeValidator(file, 15))) {
          errorHandler(t('error.fileLimit'));
          return;
        }

        const currentUserId = getNumericUserId(auth.userData);
        const uploadedByRole = getPblUploadRole(auth.userData);
        const createdDocuments: PblDocument[] = [];

        for (const file of selectedFiles) {
          const uploadResponse = await uploadPblDocumentFile(file);
          const storageFileId = getUploadedPblFileId(uploadResponse.data.data);

          if (!storageFileId) {
            errorHandler(uploadResponse.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToUploadDocument'));
            return;
          }

          const documentResponse = await createPblDocument(
            backendCase.id,
            buildPblDocumentCreatePayload({
              caseData: backendCase,
              storageFileId,
              title: file.name,
              documentType: 'signed_document',
              participantUserIds: currentUserId ? [currentUserId] : [],
              includeAssignedLawyer: true,
              metadata: {
                uploaded_by_role: uploadedByRole,
                ...(currentUserId ? { uploaded_by_user_id: currentUserId } : {}),
              },
            })
          );

          if (documentResponse.data.success === false) {
            errorHandler(documentResponse.data.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToCreateDocument'));
            return;
          }

          createdDocuments.push(documentResponse.data.data);
        }

        if (createdDocuments.length > 0) {
          const appendCreatedDocuments = () => {
            setBackendCase((prevCase) =>
              prevCase
                ? {
                    ...prevCase,
                    documents: [...(prevCase.documents || []), ...createdDocuments],
                  }
                : prevCase
            );
          };

          try {
            const caseResponse = await getPblCase(backendCase.id);

            if (caseResponse.data.success === false) {
              appendCreatedDocuments();
            } else {
              setBackendCase(caseResponse.data.data);
            }
          } catch {
            appendCreatedDocuments();
          }

          successHandler(
            createdDocuments.length === 1
              ? t('pbl.pagesHardcoded.caseProgressDetail.documentUploaded')
              : t('pbl.pagesHardcoded.caseProgressDetail.documentsUploaded')
          );
        }
      } catch (error: any) {
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.caseProgressDetail.failedToUploadDocument'));
      } finally {
        setIsUploadingDocument(false);
        setFiles([]);
      }
    },
    [auth.userData, backendCase, canUseInvitationDocumentUploads, t]
  );

  useEffect(() => {
    if (files.length > 0 && uploadFilesRef.current !== files) {
      uploadFilesRef.current = files;
      uploadInvitationDocuments(files);
      return;
    }

    if (files.length === 0) uploadFilesRef.current = null;
  }, [files, uploadInvitationDocuments]);

  if (!item) {
    return (
      <PageContainer
        title={
          <TitleBar isMobile={isMobile}>
            <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.cases)}>
              {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.back')}</Button>
            <span>
              {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.home')}<strong>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.proBonoLegalServiceCasesReview')}</strong>
            </span>
          </TitleBar>
        }
      >
        <DetailSectionPanel isTablet={isTablet}>
          <Text margin={0}>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.loadingCase')}</Text>
        </DetailSectionPanel>
      </PageContainer>
    );
  }

  if (shouldRedirectCitizenToCaseList) return null;

  if (shouldRedirectLawyerToMeetingCalendar) return null;

  if (shouldOpenCitizenRatingForm && backendCase) {
    return <PblCitizenCaseFeedback caseData={backendCase} />;
  }

  if (shouldShowCaseProgressWorkspace && backendCase) {
    return <PblCaseProgressDetail caseData={backendCase} />;
  }

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.cases)}>
            {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.home')}<strong>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.proBonoLegalServiceCasesReview')}</strong>
          </span>
        </TitleBar>
      }
    >
      <DetailSectionPanel isTablet={isTablet}>
        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.districtOfficeStaffInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.legcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.officeName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.assistingStaffNameLegcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.assistingStaffName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.officeContact')}</InfoLabel>
          <InfoValue>{item.officeContact}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.caseInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.caseId')}</InfoLabel>
          <InfoValue>{item.caseId}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.enquirer')}</InfoLabel>
          <InfoValue>{item.enquirer.fullName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.hkid4Digits')}</InfoLabel>
          <InfoValue>{item.enquirer.identityPartial}</InfoValue>
        </InfoGrid>
        <CaseInfoAction>
          <Button type="button" size="lg" variant="primary" rounded={false} onClick={() => setInfoOpen(true)}>
            {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.viewInformation')}</Button>
        </CaseInfoAction>

        {invitationPolling.waiting && (
          <MatchNoticeBox role="status" aria-live="polite">
            <Text>{t(`pbl.backlogContinuation.invitation${invitationPolling.state === 'active' ? 'Watching' : invitationPolling.state === 'paused' ? 'Paused' : 'Error'}` as any)}</Text>
            <Button type="button" variant="regular" disabled={invitationPolling.state === 'unavailable'} onClick={invitationPolling.refresh}>
              {t('pbl.backlogContinuation.refresh')}
            </Button>
          </MatchNoticeBox>
        )}
        {isManualAssignmentRequired && canManageLifecycle && (
          <MatchNoticeBox role="status" aria-live="polite">
            {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.manualAssignmentRequiredNotice')}
          </MatchNoticeBox>
        )}

        {view.mode === 'declined' ? (
          <>
            {!isManualAssignmentRequired && (
              <DeclinedIntro>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.thisCaseWasRecordedByDeclined')}</DeclinedIntro>
            )}
            <DeclinedTable>
              <thead>
                <tr>
                  <th>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.lawyerName')}</th>
                  <th>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.reason')}</th>
                  <th>{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.date')}</th>
                </tr>
              </thead>
              <tbody>
                {(item.declinedLawyers || []).map((declinedLawyer) => (
                  <tr key={`${declinedLawyer.lawyerName}-${declinedLawyer.date}`}>
                    <td>{declinedLawyer.lawyerName}</td>
                    <td>{declinedLawyer.status === 'timeout' && declinedLawyer.reason === 'Declined'
                      ? t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.invitationTimedOut')
                      : declinedLawyer.reason}</td>
                    <td>{declinedLawyer.date}</td>
                  </tr>
                ))}
              </tbody>
            </DeclinedTable>
            {matchedLawyer && !isPendingReassignment && !isManualAssignmentRequired ? (
              <>
                <MatchNoticeBox>
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.theLawyerShownBelowIsRecommendedByThe')}<br />
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.caseRelevance')}<br />
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.theDutyRecordsOfTheProBonoLawyer')}<br />
                  <br />
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.finalConfirmationRemainsWithTheLegcoMemberS')}</MatchNoticeBox>
                <div ref={recommendationRef}>
                  <LawyerProfileCard lawyer={matchedLawyer} onPreview={openPreview} />
                </div>
              </>
            ) : null}
            <ActionRow isMobile={isMobile}>
              {canRunMatchingAgain && (
                <Button
                  type="button"
                  size="lg"
                  variant={canInviteFirstMatchedLawyer ? 'primary-invert' : 'primary'}
                  rounded={false}
                  disabled={isRunningMatching || isMatchingProcessing}
                  onClick={runMatchingAgain}
                >
                  {isRunningMatching || isMatchingProcessing ? t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.runningMatching') : t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.runMatchingAgain')}
                </Button>
              )}
              {canInviteFirstMatchedLawyer && (
                <Button
                  type="button"
                  size="lg"
                  variant="primary"
                  rounded={false}
                  disabled={isInviting}
                  onClick={openInviteConfirm}
                >
                  {isInviting ? t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.sending') : t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.sendInvitation')}
                </Button>
              )}
              {canChooseAnotherLawyer && (
                <Button
                  type="button"
                  size="lg"
                  variant="primary"
                  rounded={false}
                  disabled={isReassigningLawyer}
                  onClick={chooseAnotherLawyer}
                >
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.chooseAnotherLawyer')}</Button>
              )}
              {canReassignLawyer && (
                <Button
                  type="button"
                  size="lg"
                  variant="primary-invert"
                  rounded={false}
                  disabled={isReassigningLawyer}
                  onClick={() => setShowReassignLawyer(true)}
                >
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.reassignLawyer')}</Button>
              )}
              {canCancelCase && (
                <Button
                  type="button"
                  size="lg"
                  variant="danger"
                  rounded={false}
                  disabled={isCancellingCase}
                  onClick={() => setShowCancelCaseConfirm(true)}
                >
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.cancelCase')}</Button>
              )}
            </ActionRow>
          </>
        ) : (
          <>
            {showMatchingContext && (view.mode === 'ai_recommended' || view.mode === 'invitation_sent') ? (
              <MatchNoticeBox>
                {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.theLawyerShownBelowIsRecommendedByThe')}<br />
                {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.caseRelevance')}<br />
                {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.theDutyRecordsOfTheProBonoLawyer')}<br />
                <br />
                {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.finalConfirmationRemainsWithTheLegcoMemberS')}</MatchNoticeBox>
            ) : null}

            {isManualAssignmentRequired ? null : isMatchingProcessing ? (
              <MatchingProgressPanel
                ref={matchingHeadingRef}
                status={effectiveMatchingStatus}
                elapsedMs={matchingPolling.elapsedMs}
                consecutiveFailures={matchingPolling.consecutiveFailures}
                autoStopped={matchingPolling.autoStopped}
                onCheckStatus={matchingPolling.checkNow}
              />
            ) : isMatchingFailed ? (
              <MatchNoticeBox role="status" aria-live="polite" aria-atomic="true">
                {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.matchingFailedNeutral')}
              </MatchNoticeBox>
            ) : isMatchingCompletedWithoutResult ? (
              <MatchNoticeBox role="status" aria-live="polite" aria-atomic="true">
                {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.matchingCompletedWithoutResult')}
              </MatchNoticeBox>
            ) : displayLawyer ? (
              <div ref={showMatchingContext ? recommendationRef : undefined}>
                <LawyerProfileCard
                  lawyer={displayLawyer}
                  onPreview={openPreview}
                />
              </div>
            ) : hasCurrentAssignment ? null : (
              <Text margin="18px 0">{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.noLawyerRecommendationIsAvailableForThisCase')}</Text>
            )}

            {view.mode === 'invitation_sent' ? (
              <>
                <InvitationNotice>
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.yourInvitationToTheAboveLawyersHasBeen')}<br />
                  {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.pleaseWaitForConfirmationFromTheLawyerUpon')}</InvitationNotice>
                {canUseInvitationDocumentUploads && backendCase && (
                  <div style={{ marginTop: 24 }}>
                    <ContractNegotiationPanel
                      documents={backendCase.documents || []}
                      canManageCase={canUseInvitationDocumentUploads}
                      canDownloadDocument={canManageLifecycle}
                      files={files}
                      setFiles={setFiles}
                      onDragStateChange={onDragStateChange}
                      onFilesDrop={onFilesDrop}
                      fileNamePatternText={t('pattern.fileName')}
                      isUploading={isUploadingDocument}
                      signingDocumentId={null}
                      loadingDocumentId={loadingDocumentId}
                      onInspectDocument={inspectDocument}
                      onDownloadDocument={downloadDocument}
                      onInviteToSign={async () => {}}
                    />
                  </div>
                )}
                {(canCancelCase || canReassignLawyer || canChooseAnotherLawyer) && (
                  <ActionRow isMobile={isMobile} style={{ marginTop: 28 }}>
                    {canCancelCase && (
                      <Button
                        type="button"
                        size="lg"
                        variant="danger"
                        rounded={false}
                        disabled={isCancellingCase}
                        onClick={() => setShowCancelCaseConfirm(true)}
                      >
                        {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.cancelCase')}</Button>
                    )}
                    {canReassignLawyer && (
                      <Button
                        type="button"
                        size="lg"
                        variant="primary-invert"
                        rounded={false}
                        disabled={isReassigningLawyer}
                        onClick={() => setShowReassignLawyer(true)}
                      >
                        {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.reassignLawyer')}</Button>
                    )}
                    {canChooseAnotherLawyer && (
                      <TextLinkButton type="button" onClick={chooseAnotherLawyer}>
                        {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.chooseAnotherLawyer')}</TextLinkButton>
                    )}
                  </ActionRow>
                )}
              </>
            ) : (
              (canInviteFirstMatchedLawyer || canCancelCase || canReassignLawyer || canChooseAnotherLawyer) && (
                <ActionRow isMobile={isMobile} style={{ marginTop: 28 }}>
                  {canInviteFirstMatchedLawyer && (
                    <Button
                      type="button"
                      size="lg"
                      variant="primary"
                      rounded={false}
                      disabled={isInviting}
                      onClick={openInviteConfirm}
                    >
                      {isInviting ? t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.sending') : t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.inviteSuggestedLawyer')}
                    </Button>
                  )}
                  {canCancelCase && (
                    <Button
                      type="button"
                      size="lg"
                      variant="danger"
                      rounded={false}
                      disabled={isCancellingCase}
                      onClick={() => setShowCancelCaseConfirm(true)}
                    >
                      {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.cancelCase')}</Button>
                  )}
                  {canReassignLawyer && (
                    <Button
                      type="button"
                      size="lg"
                      variant="primary-invert"
                      rounded={false}
                      disabled={isReassigningLawyer}
                      onClick={() => setShowReassignLawyer(true)}
                    >
                      {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.reassignLawyer')}</Button>
                  )}
                  {canChooseAnotherLawyer && (
                    <TextLinkButton type="button" onClick={chooseAnotherLawyer}>
                      {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.chooseAnotherLawyer')}</TextLinkButton>
                  )}
                </ActionRow>
              )
            )}
          </>
        )}
      </DetailSectionPanel>
      <LawyerReviewSummaryModal
        lawyer={displayLawyer}
        language={previewLanguage}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
      <CaseInformationModal item={item} open={infoOpen} onClose={() => setInfoOpen(false)} />
      <DocumentDetailModal
        showModal={showDocumentDetail}
        setShowModal={setShowDocumentDetail}
        document={documentDetail}
        isLoading={Boolean(loadingDocumentId)}
        onDownloadDocument={downloadDocument}
      />
      <CancelCaseConfirmModal
        showModal={showCancelCaseConfirm}
        setShowModal={setShowCancelCaseConfirm}
        isSubmitting={isCancellingCase}
        onConfirm={confirmCancelCase}
      />
      <ReassignLawyerModal
        showModal={showReassignLawyer}
        setShowModal={setShowReassignLawyer}
        isSubmitting={isReassigningLawyer}
        onConfirm={confirmReassignLawyer}
      />
      <Modal
        open={showInviteConfirm}
        onClose={() => {
          if (!isInviting) setShowInviteConfirm(false);
        }}
        aria-labelledby="invite-first-matched-lawyer-title"
      >
        <ModalSurface>
          <ModalTitle id="invite-first-matched-lawyer-title">{t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.chooseALawyer')}</ModalTitle>
          <ChoiceWarningNotice role="alert">
            {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.onceALawyerIsSelectedThisChoiceCannot')}</ChoiceWarningNotice>
          <ModalAction>
            <Button
              type="button"
              size="lg"
              variant="primary"
              rounded={false}
              disabled={isInviting}
              onClick={sendInvitation}
            >
              {isInviting ? t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.choosing') : t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.choose')}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              disabled={isInviting}
              onClick={() => setShowInviteConfirm(false)}
            >
              {t('pbl.pagesHardcoded.ocManageCases.manageCaseDetail.cancel')}</Button>
          </ModalAction>
        </ModalSurface>
      </Modal>
    </PageContainer>
  );
};

export default PblOcManageCaseDetail;
